import { Box, Button, Divider, FormControl, VStack } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useEffect, useState } from 'react';
import { resetPassword, validatePassword } from '../../api/auth';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobile } from '../../hooks/UseResponsive/UseResponsive';
import CanopyButton from '../CanopyButton/CanopyButton';
import CanopyInput from '../CanopyInput/CanopyInput';
import ErrorBox from '../ErrorBox/ErrorBox';
import { LoginFlow, LoginPageProps } from './LoginPage';

interface LoginFlowProps extends LoginPageProps {
    setLoginFlow: React.Dispatch<React.SetStateAction<LoginFlow>>;
    user: any;
}
const ResetPassword: React.FC<LoginFlowProps> = (props) => {
    const currentColors = useCurrentColors();
    const isMobile = useCheckForMobile();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [canSkipResetPassword, setCanSkipResetPassword] = useState(false);
    const [success, setSuccess] = useState('');
    const { t, user } = props;

    const debouncedConfirmPassword = debounce(setConfirmPassword, 300);

    const handleResetPassword = async (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();
        try {
            const response: any = await resetPassword(password, user);
            if (response.error) {
                setError(response.error);
            } else if (response.status === 'ok') {
                setError('');
                props.setLoginFlow('login');
                props?.setSuccess &&
                    props?.setSuccess(t('notify.password-reset-success'));
            }
        } catch (e: any) {
            if (e === 'unexpected-error') {
                setError(t('notification.unexpected-error'));
            } else {
                setError(e);
            }
        }
    };

    useEffect(() => {
        if (user.days_to_password_expiry > 0) {
            setCanSkipResetPassword(true);
        }
    }, [user.days_to_password_expiry]);

    useEffect(() => {
        if (!password) {
            setError('');
            return;
        }

        if (password.trim().length < 8) {
            setError(t('password-requirements-tip'));
            return;
        }
        if (
            user?.username &&
            password.toLowerCase().includes(user?.username.toLowerCase())
        ) {
            setError(t('password-requirements-tip'));
            return;
        }

        if (password && confirmPassword) {
            confirmPassword !== password
                ? setError(t('validations.passwords-dont-match'))
                : setError('');
            return;
        }

        (async () => {
            try {
                const response: any = await validatePassword(
                    password,
                    user.username
                );
                if (response.error) {
                    setError(response.error);
                } else if (response === true) {
                    setError('');
                }
            } catch (e: any) {
                e === 'generic-problem'
                    ? setError(t('error.generic-problem'))
                    : setError(e);
            }
        })();
    }, [password, confirmPassword]);

    return (
        <>
            {canSkipResetPassword ? (
                <>
                    <Box
                        color={currentColors.contentText}
                        mx="auto"
                        width="300px"
                        fontSize="15px"
                        mt="5px"
                        textAlign="center">
                        {t('login-password-session.password-expire-in-days', {
                            days: user.days_to_password_expiry,
                        })}
                    </Box>
                    <Box
                        color={currentColors.contentText}
                        mx="auto"
                        width="300px"
                        fontSize="15px"
                        textAlign="center">
                        {t(
                            'login-password-session.reset-password-message-now-later'
                        )}
                    </Box>
                </>
            ) : (
                <>
                    <Box
                        color={currentColors.contentText}
                        mx="auto"
                        width="300px"
                        fontSize="15px"
                        mt="5px"
                        textAlign="center">
                        {t('login-password-session.password-expired-message')}
                    </Box>
                    <Box
                        color={currentColors.contentText}
                        mx="auto"
                        width="300px"
                        fontSize="15px"
                        textAlign="center">
                        {t(
                            'login-password-session.change-password-to-continue'
                        )}
                    </Box>
                </>
            )}

            <Divider
                borderColor={currentColors.brand['700']}
                width="330px"
                my="20px"
            />
            <form
                onSubmit={handleResetPassword}
                style={{
                    width: isMobile ? '342px' : 'initial',
                    marginLeft: isMobile ? '7px' : 'initial',
                    marginRight: isMobile ? '7px' : 'initial',
                }}>
                <VStack spacing="26px">
                    {error && (
                        <ErrorBox
                            p="0"
                            m="0"
                            backgroundColor="inherit"
                            color={currentColors.errorText}
                            fontSize="14px"
                            fontWeight="700"
                            position="relative"
                            mb="20px"
                            textAlign="center"
                            error={error}></ErrorBox>
                    )}
                    <FormControl px={{ sm: '15px' }}>
                        <CanopyInput
                            id="password"
                            type="password"
                            placeholder={t('password')}
                            label={t('password')}
                            width={[
                                '268px',
                                '315px',
                                '315px',
                                '420px',
                                '420px',
                            ]}
                            height="45px"
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => setPassword(event.target.value)}></CanopyInput>
                    </FormControl>
                    <FormControl
                        mt="31px"
                        px={{ sm: '15px' }}>
                        <CanopyInput
                            id="confirmPassword"
                            type="password"
                            //placeholder={t('password')}
                            label={t('confirm-password')}
                            width={[
                                '268px',
                                '315px',
                                '315px',
                                '420px',
                                '420px',
                            ]}
                            height="45px"
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) =>
                                debouncedConfirmPassword(event.target.value)
                            }></CanopyInput>
                    </FormControl>

                    <CanopyButton
                        mx={{ sm: '15px' }}
                        type="submit"
                        height="45px"
                        width={['268px', '315px', '315px', '420px', '420px']}
                        mt="26px"
                        isDisabled={
                            !password || !confirmPassword || Boolean(error)
                        }>
                        {t('reset-password')}
                    </CanopyButton>
                </VStack>
            </form>
            {!canSkipResetPassword && (
                <Button
                    fontSize="lg"
                    variant="link"
                    width={['50%', '50%', '50%', '120px', '120px']}
                    height="20px"
                    mt="20px"
                    color={currentColors.brand['300']}
                    _hover={{ textDecoration: 'none' }}
                    _focus={{ border: 'none' }}
                    onClick={() => props.setLoginFlow('login')}>
                    {t('forgot-password.return-to-login')}
                </Button>
            )}
            {canSkipResetPassword && (
                <Button
                    fontSize="lg"
                    variant="link"
                    width={['50%', '50%', '50%', '120px', '120px']}
                    height="20px"
                    mt="20px"
                    color={currentColors.brand['300']}
                    _hover={{ textDecoration: 'none' }}
                    _focus={{ border: 'none' }}
                    onClick={() => props.setLoginFlow('logged_in')}>
                    {t('login-password-session.skip-and-continue')}
                </Button>
            )}
        </>
    );
};

export default ResetPassword;
